import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { trackPageView } from '../utils/analytics';

const NotFoundPage = () => {
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [smoothMousePos, setSmoothMousePos] = useState({ x: 0, y: 0 });
    const [randomMessage, setRandomMessage] = useState('');

    useEffect(() => {
        // Initialiser le message aléatoire une seule fois au montage
        const messages = [
            "La page s'est perdue sur le web... comme un vieux site sans refonte 😅",
            "Vous cherchiez quelque chose ? Laissez-nous vous créer LA page parfaite",
            "Cette page a disparu. Contrairement à vos concurrents!"
        ];
        setRandomMessage(messages[Math.floor(Math.random() * messages.length)]);
        trackPageView('/404');
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            setMousePos({
                x: (e.clientX / window.innerWidth) * 10 - 5,
                y: (e.clientY / window.innerHeight) * 10 - 5
            });
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    // Smooth animation loop pour les transitions fluides
    useEffect(() => {
        let animationId;
        const animate = () => {
            setSmoothMousePos(prev => ({
                x: prev.x + (mousePos.x - prev.x) * 0.1,
                y: prev.y + (mousePos.y - prev.y) * 0.1
            }));
            animationId = requestAnimationFrame(animate);
        };
        animationId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(animationId);
    }, [mousePos]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#2f3b52] via-[#3d4e68] to-[#4a5a7d] flex items-center justify-center px-4 py-12 overflow-hidden relative">
            {/* Animated background circles */}
            <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl" style={{ animationDelay: '2s' }}></div>

            {/* Grid pattern */}
            <div
                className="absolute inset-0 opacity-10"
                style={{
                    backgroundImage:
                        'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
                    backgroundSize: '50px 50px',
                }}
            ></div>

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#2f3b52]/50 to-[#2f3b52]"></div>

            {/* Content */}
            <div className="relative z-10 text-center max-w-2xl mx-auto">
                {/* Animated 404 Text */}
                <div
                    className="relative mb-8 inline-block"
                    style={{
                        transform: `perspective(1000px) rotateX(${smoothMousePos.y}deg) rotateY(${smoothMousePos.x}deg)`,
                        transition: 'transform 0.05s linear'
                    }}
                >
                    <h1 className="text-8xl md:text-9xl font-black text-white drop-shadow-2xl">
                        <span className="bg-gradient-to-r from-[#f7876f] to-[#e97058] bg-clip-text text-transparent">
                            4
                        </span>
                        <span className="text-white">0</span>
                        <span className="bg-gradient-to-r from-[#f7876f] to-[#e97058] bg-clip-text text-transparent">
                            4
                        </span>
                    </h1>
                </div>

                {/* Subtitle */}
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow-lg">
                    Oups ! Page non trouvée
                </h2>

                {/* Fun message */}
                <p className="text-lg md:text-xl text-blue-100 mb-8 italic drop-shadow-lg">
                    {randomMessage}
                </p>

                {/* Subtitle explanation */}
                <p className="text-white/70 mb-12 text-sm md:text-base">
                    Mais ne vous inquiétez pas, nous pouvons créer quelque chose de magnifique pour vous ! ✨
                </p>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    {/* Back Home */}
                    <Link
                        to="/"
                        className="px-8 py-4 rounded-lg font-bold text-lg bg-white/10 text-white border-2 border-white/30 hover:border-[#f7876f] hover:bg-white/20 transform hover:scale-105 transition-all duration-300"
                    >
                        ← Retour à l'accueil
                    </Link>

                    {/* CTA to Order */}
                    <Link
                        to="/order"
                        className="px-8 py-4 rounded-lg font-bold text-lg bg-gradient-to-r from-[#f7876f] to-[#e97058] text-white hover:from-[#e97058] hover:to-[#d96049] transform hover:scale-105 transition-all duration-300 shadow-lg"
                    >
                        Créer mon site parfait 🚀
                    </Link>
                </div>

                {/* Extra fun element */}
                <div className="mt-12 text-6xl animate-bounce">
                    🔍
                </div>
                <p className="text-white/50 text-sm mt-4">
                    Cette page est introuvable... comme un bon design en 2026 sans refonte
                </p>
            </div>

            <style jsx>{`
                @keyframes float {
                    0%, 100% {
                        transform: translateY(0px);
                    }
                    50% {
                        transform: translateY(-20px);
                    }
                }
            `}</style>
        </div>
    );
};

export default NotFoundPage;
